import {
	getCollateral,
	type TDReport10,
	type VerifiedReport,
	verify,
} from "@phala/dcap-qvl";
import { decodeBytes } from "open-signer-shared-cryptography";
import { measureFunctionTime } from "../../common/utils";
import type { FrameLogger } from "../../logger";
import type { AttestationBundle } from "../types";
import type { TEEQuoteVerifier } from "./verifier";

const PCCS_URL = "https://pccs.phala.network/tdx/certification/v4";
const ATTESTATION_VERIFIED_STATUS = "UpToDate";

export class DstackVerifier implements TEEQuoteVerifier<VerifiedReport> {
	constructor(private readonly logger: FrameLogger) {}

	logPrefix = "Dstack Verifier";
	name = "Dstack";
	/**
	 * Verifies the Intel TDX TEE attestation report using quote verification.
	 *
	 * This method performs the core hardware attestation validation by:
	 * 1. Decoding the attestation quote from hexadecimal format
	 * 2. Retrieving cryptographic collateral from Phala's PCCS service
	 * 3. Verifying the quote authenticity and recency using Intel's verification logic
	 * 4. Validating that the TEE attestation status is current and trusted
	 *
	 * The verification process cryptographically proves that:
	 * - The code is running in a genuine Intel TDX Trusted Execution Environment
	 * - The TEE measurements and configuration are authentic and unmodified
	 * - The attestation was generated recently (prevents replay attacks)
	 *
	 * @param quote - Base64-encoded Intel TDX attestation quote containing TEE measurements
	 * @returns Promise resolving to validated attestation report with TEE measurements and status
	 * @throws {Error} When quote verification fails or collateral retrieval fails
	 * @throws {Error} When TEE attestation status is not 'UpToDate' (indicating untrusted TEE)
	 */
	async verifyTEEReport(bundle: AttestationBundle) {
		const decodedQuote = decodeBytes(bundle.quote, "hex");

		const currentTime = Math.floor(Date.now() / 1000);
		const { time: jsVerifyTime, result: report } = await measureFunctionTime(
			() => verify(decodedQuote, bundle.collateral, currentTime),
		);
		this.logger.debug(
			this.logPrefix,
			`Attestation verified in ${jsVerifyTime}ms`,
		);

		if (report.status !== ATTESTATION_VERIFIED_STATUS) {
			throw new Error("TEE attestation is invalid");
		}

		return report;
	}

	extractTDFromReport(report: VerifiedReport) {
		const r = report.report;
		let td: TDReport10;

		switch (r.type) {
			case "td10": {
				const _td = r.asTd10();
				if (!_td) {
					throw new Error("Empty TEE report. Aborting...");
				}
				td = _td;
				break;
			}
			case "td15": {
				const _td = r.asTd15()?.base;
				if (!_td) {
					throw new Error("Empty TEE report. Aborting...");
				}
				td = _td;
				break;
			}
			default: {
				throw new Error(
					"Unsupported type returned in the TEE report. Aborting...",
				);
			}
		}
		if (!td.reportData) {
			throw new Error("Missing reportData in TD report");
		}
		if (!td.rtMr3) {
			throw new Error("Missing rtMr3 in TD report");
		}

		const rtMr3 = this.ensureHex(td.rtMr3);
		const reportData = this.ensureHex(td.reportData);

		return {
			reportData,
			rtMr3,
		};
	}

	async verifyTEEReportAndExtractTD(
		bundle: AttestationBundle,
	): Promise<{ reportData: string; rtMr3: string }> {
		const verifiedQuote = await this.verifyTEEReport(bundle);
		return this.extractTDFromReport(verifiedQuote);
	}

	private ensureHex(val: Uint8Array<ArrayBufferLike>): string {
		return Array.from(val)
			.map((byte) => byte.toString(16).padStart(2, "0"))
			.join("");
	}
}
